import dayjs, { Dayjs } from 'dayjs';
import logger from '../../utils/logger';
import utc from 'dayjs/plugin/utc';
import { Box } from '@mui/material';
import { palette } from '../../theme/palette';
import {
  DatePickerContainer,
  DateSelectContainer,
  DateTimeGridContainer,
  DateTimeRow,
  StyledDatePicker,
  TimePickerContainer,
  TimeSelect,
} from './DateRangePickerStyle';

dayjs.extend(utc);

export interface DateRangePickerProps {
  minStartDate: Dayjs;
  startDate: Dayjs | null | unknown;
  endDate: Dayjs | null | unknown;
  setStartDate: (date: Dayjs) => void;
  setEndDate: (date: Dayjs) => void;
  startDateLabel?: JSX.Element;
  endDateLabel?: JSX.Element;
  startDateHelperLabel?: JSX.Element;
  endDateHelperLabel?: JSX.Element;
  startTime?: string;
  endTime?: string;
  setStartTime?: (time: string) => void;
  setEndTime?: (time: string) => void;
  startTimeLabel?: JSX.Element;
  endTimeLabel?: JSX.Element;
}

export default function DateRangePicker({
  minStartDate,
  startDate,
  endDate,
  setStartDate,
  setEndDate,
  startDateLabel,
  endDateLabel,
  startDateHelperLabel,
  endDateHelperLabel,
  startTime,
  endTime,
  setStartTime,
  setEndTime,
  startTimeLabel,
  endTimeLabel,
  ...rest
}: DateRangePickerProps) {
  const handleStartDateChange = (date: Dayjs) => {
    minStartDate = dayjs
      .utc(String(minStartDate))
      .local()
      .set('hour', 0)
      .set('minute', 0)
      .set('second', 0);
    date = dayjs.utc(String(date));
    if (endDate && date && date.isAfter(dayjs(String(endDate)))) {
      handleSetStartDate(date);
      handleSetEndDate(date.add(1, 'day'));
    } else if (date.isAfter(minStartDate) || date.isSame(minStartDate)) {
      handleSetStartDate(date);
      if(!endDate) handleSetEndDate(date.add(1, 'day'))
    } else {
      logger('error', 'trying to assign invalid start date');
    }
  };

  const handleEndDateChange = (date: Dayjs) => {
    minStartDate = dayjs
      .utc(String(minStartDate))
      .local()
      .set('hour', 0)
      .set('minute', 0)
      .set('second', 0);
    date = dayjs.utc(String(date));
    if (
      startDate &&
      date &&
      date.isBefore(dayjs(String(startDate))) &&
      (date.isAfter(dayjs(String(minStartDate))) || date.isSame(dayjs(String(minStartDate)), 'day'))
    ) {
      handleSetStartDate(date);
      handleSetEndDate(date);
    } else if (date.isBefore(minStartDate)) {
      logger('error', 'trying to assign invalid end date');
    } else {
      handleSetEndDate(date);
    }
  };

  const handleSetStartDate = (date: Dayjs) => {
    setStartDate(date.local().startOf('day'));
  };

  const handleSetEndDate = (date: Dayjs) => {
    setEndDate(date.local().endOf('day'));
  };

  const hasTimeSelection = !!(startTime !== undefined && setStartTime && endTime !== undefined && setEndTime);

  if (hasTimeSelection) {
    return (
      <DateTimeGridContainer {...rest}>
        <DateTimeRow>
          <DatePickerContainer className='date-picker-start-date-container'>
            {startDateLabel && <Box>{startDateLabel}</Box>}
            <StyledDatePicker
              minDate={minStartDate}
              value={startDate}
              dateChangeHandler={handleStartDateChange}
              showIcon
              iconColor={palette.primary.main}
              isStyledForFormDatePicker
              format='DD MMM YYYY'
            />
            {startDateHelperLabel && <Box>{startDateHelperLabel}</Box>}
          </DatePickerContainer>

          <TimePickerContainer>
            {startTimeLabel && <Box>{startTimeLabel}</Box>}
            <TimeSelect value={startTime} onChange={setStartTime} />
          </TimePickerContainer>
        </DateTimeRow>

        <DateTimeRow>
          <DatePickerContainer className='date-picker-end-date-container'>
            {endDateLabel && <Box>{endDateLabel}</Box>}
            <StyledDatePicker
              minDate={minStartDate}
              value={endDate}
              dateChangeHandler={handleEndDateChange}
              showIcon
              iconColor={palette.primary.main}
              isStyledForFormDatePicker
              format='DD MMM YYYY'
            />
            {endDateHelperLabel && <Box>{endDateHelperLabel}</Box>}
          </DatePickerContainer>

          <TimePickerContainer>
            {endTimeLabel && <Box>{endTimeLabel}</Box>}
            <TimeSelect value={endTime} onChange={setEndTime} />
          </TimePickerContainer>
        </DateTimeRow>
      </DateTimeGridContainer>
    );
  }

  return (
    <DateSelectContainer {...rest}>
      <DatePickerContainer className='date-picker-start-date-container'>
        {startDateLabel && <Box>{startDateLabel}</Box>}
        <StyledDatePicker
          minDate={minStartDate}
          value={startDate}
          dateChangeHandler={handleStartDateChange}
        ></StyledDatePicker>
        {startDateHelperLabel && <Box>{startDateHelperLabel}</Box>}
      </DatePickerContainer>

      <DatePickerContainer className='date-picker-end-date-container'>
        {endDateLabel && <Box>{endDateLabel}</Box>}
        <StyledDatePicker
          minDate={minStartDate}
          value={endDate}
          dateChangeHandler={handleEndDateChange}
        ></StyledDatePicker>
        {endDateHelperLabel && <Box>{endDateHelperLabel}</Box>}
      </DatePickerContainer>
    </DateSelectContainer>
  );
}
